import { useSelector } from 'react-redux';
import { RootState } from '@/reducers';
import { CalendarEventType } from '../types';

export interface CalendarDepartment {
	_id: string;
	name: string;
	_business?: string;
	_users?: (string | { _id: string; active?: boolean })[];
}

/** Departments of the signed-in location (enterprise accounts also load other locations') */
export const useLocationDepartments = (): CalendarDepartment[] => {
	const _business = useSelector((state: RootState) => state.auth?._id as string | undefined);
	const departments = useSelector(
		(state: RootState) => (state.departments || []) as CalendarDepartment[]
	);
	return departments.filter(
		(department) => !department._business || department._business === _business
	);
};

/** Active members of a department (the API populates `_users`) */
export const getDepartmentUserIds = (department: CalendarDepartment): string[] =>
	(department._users || [])
		.filter((user) => typeof user === 'string' || user.active !== false)
		.map((user) => (typeof user === 'string' ? user : user._id));

/** Adds every department member to the current assignees, without duplicates */
export const addDepartmentToAssignees = (
	assignees: string[],
	department: CalendarDepartment
): string[] =>
	Array.from(new Set([...assignees, ...getDepartmentUserIds(department)]));

/**
 * Everyone an item belongs to. A linked department counts its current members,
 * so people who join it later see the event too.
 */
export const getEventAssigneeIds = (
	event: CalendarEventType,
	departments: CalendarDepartment[]
): string[] => {
	if (event.job) return (event.job._assignees || []).filter(Boolean) as string[];
	if (event.calendarEvent) {
		const department = departments.find(
			(d) => d._id === event.calendarEvent?._department
		);
		return Array.from(
			new Set([
				...(event.calendarEvent._assignees || []),
				...(department ? getDepartmentUserIds(department) : []),
			])
		);
	}
	if (event.reminder) {
		if (event.reminder._assignees?.length) return event.reminder._assignees;
		const owner = event.reminder._assignee || event.reminder._user;
		return owner ? [owner] : [];
	}
	return [];
};

/**
 * Keeps jobs, events and reminders assigned to a selected person, plus items
 * with no one assigned when `showUnassigned` is on
 */
export const filterEventsByPeople = (
	events: CalendarEventType[],
	departments: CalendarDepartment[],
	_selectedUsers: string[],
	showUnassigned: boolean
): CalendarEventType[] => {
	if (_selectedUsers.length === 0 && !showUnassigned) return events;

	return events.filter((event) => {
		const assigneeIds = getEventAssigneeIds(event, departments);
		if (showUnassigned && assigneeIds.length === 0) return true;
		return assigneeIds.some((id) => _selectedUsers.includes(id));
	});
};

/** Keeps jobs, events and reminders linked to, or assigned to someone in, the selected departments */
export const filterEventsByDepartments = (
	events: CalendarEventType[],
	departments: CalendarDepartment[],
	_selectedDepartments: string[]
): CalendarEventType[] => {
	if (_selectedDepartments.length === 0) return events;

	const memberIds = new Set(
		departments
			.filter((department) => _selectedDepartments.includes(department._id))
			.flatMap(getDepartmentUserIds)
	);

	return events.filter(
		(event) =>
			_selectedDepartments.includes(event.calendarEvent?._department || '') ||
			getEventAssigneeIds(event, departments).some((id) => memberIds.has(id))
	);
};
